import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const PRICING = "https://www.veed.io/pricing";
const ARTICLE = "https://www.veed.io/learn/veed-vs-capcut";

export const VEED_CONTENT: CompareContent = {
  slug: "crayo-vs-veed",
  competitorSlug: "veed",
  title: "Crayo vs VEED",
  seoTitle: "Crayo vs VEED: Pricing, Credits and Clipping Compared",
  seoDescription:
    "Crayo vs VEED with dated prices. VEED showed Creator, Pro and Studio in CAD on Oct 9, 2026; yearly credits reset at renewal. Crayo publishes USD prices.",
  cardVerdict: "Better than Crayo for free tier and avatars, behind on published pricing and per-clip metering.",
  bestFor: "Teams wanting a full suite with avatars",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip long videos into shorts and want ranked moments, a face-aware camera, a timeline editor on every plan, published USD prices and credits that reset monthly, all on one ${money(hobby.monthly)} plan. VEED is the better pick if you need a free tier, avatars, dubbing, an iOS app, team seats or 4K export. The evidence, with dated sources, is below.`,
  pickCrayoIf: [
    "You clip podcasts and streams and want one credit to equal one finished clip: AutoClip returns 1 to 20 ranked clips with a face-aware camera, while VEED does not publish how many credits one clip costs.",
    `You want a price you can read before you sign up: Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year in plain HTML, while VEED's pricing page renders prices with JavaScript and showed Canadian dollars from Canada on Oct 9, 2026.`,
    `You want credits that reset every month: Crayo Hobby gives ${hobby.credits} workflow credits a month, while VEED's yearly plans hand over a year's credits up front and reset them at renewal.`,
  ],
  pickThemIf: [
    "You need a free tier: VEED's Free plan gives 10 minutes of video a month at 720p with a watermark. Crayo has no free plan and no trial.",
    "You need avatars, voice cloning, dubbing or translation to 50+ languages. Crayo has none of these.",
    "You work in a team or on an iPhone: VEED prices per user with Teams and Enterprise tiers and has an iOS app. Crayo has no team seats and is a browser app.",
  ],
  wins: [
    {
      claim: "Crayo's clipper is built for shorts; VEED's AI Clip Generator is one tool inside a general suite.",
      evidence:
        "Crayo AutoClip runs 1 to 20 clips per source, 15 to 120 seconds each, in speaker, streamer, split or full layouts at 9:16, 1:1 or 4:5, with a face-aware camera, and every clip opens in the timeline editor on every plan. VEED's clip generator page describes a 0 to 10 score on four factors and does not state face-aware reframing for clips.",
      href: "https://www.veed.io/tools/auto-video-editor/ai-clip-generator",
    },
    {
      claim: "Crayo credits reset monthly; VEED's yearly plans front-load a year's credits that reset at renewal.",
      evidence:
        "On Oct 9, 2026 the VEED pricing page listed 6,000 credits a year on Creator yearly and 30,000 a year on Pro yearly, with a FAQ heading How do AI credits work?. Fluxnote's guide, updated Sep 23, 2026, states the 30,000 credits reset at renewal and unused ones are forfeited. Crayo resets every plan's credits each month, one credit per finished clip.",
      href: "https://fluxnote.io/guides/veed-io-pricing-2026",
    },
    {
      claim: "Crayo publishes its prices in plain HTML; VEED's prices are rendered by JavaScript and differ across VEED's own pages.",
      evidence:
        "The raw HTML of veed.io/pricing contains no prices. A browser check on Oct 9, 2026 showed Creator, Pro and Studio in Canadian dollars, while VEED's own veed-vs-capcut article still lists Lite at $19 a month and Pro at $49. Crayo's pricing page lists monthly and yearly prices in US dollars for every plan.",
      href: PRICING,
    },
    {
      claim: "Crayo cancels in one click from Settings and says so on its own site.",
      evidence:
        "VEED's pricing page does not describe how to cancel as of Oct 9, 2026. Crayo's policy is published: cancel from Settings, the plan stays active to the end of the cycle, nothing further is charged.",
      href: "https://crayo.ai/refund-policy",
    },
  ],
  concessions: [
    {
      claim: "VEED has a real free tier.",
      evidence:
        "VEED's own article lists Free at $0 with 10 minutes of video a month, 720p export, a watermark and 2 minutes of auto-subtitles, and the live page offers Start for free. Crayo has no free plan and no trial.",
      href: ARTICLE,
    },
    {
      claim: "VEED has avatars, voice cloning, dubbing and AI video generation.",
      evidence:
        "VEED Pro lists translation to 50+ languages and up to 144 hours a year of AI voice on the live page checked Oct 9, 2026, and its site sells avatars and AI video models. Crayo offers hundreds of voiceover voices but no cloning, avatars or dubbing.",
      href: PRICING,
    },
    {
      claim: "VEED has an iOS app, per-seat team plans and a broader API.",
      evidence:
        "VEED sells per-user pricing with Teams and Enterprise tiers, an iOS app with Android listed as in development, and the Fabric and subtitle APIs. Crayo is a browser app with one API key per account and no team seats.",
      href: "https://www.veed.io",
    },
    {
      claim: "VEED Pro exports 4K and accepts 120-minute uploads.",
      evidence:
        "VEED's veed-vs-capcut article claims 4K on Pro, and the live page lists 4K Ultra HD exports; the same article caps Pro uploads at 120 minutes. Crayo exports up to 1080p and accepts sources up to 3 hours or 4 GB.",
      href: ARTICLE,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly, in USD`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Creator",
      cost: "CA$29/mo monthly or CA$14/mo billed yearly as CA$171",
      usage:
        "Creator CA$29/mo monthly; credits per clip not published. 500 credits a month on monthly billing, 6,000 a year on yearly, but VEED does not state how many credits one clip costs, so the share used is unknown.",
      note: "Rendered in Canadian dollars on Oct 9, 2026. Fluxnote lists a 25-minute source cap on the older Lite plan and 120 minutes on Pro; the Creator cap was not shown.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "VEED Creator and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "VEED's yearly plans carry the whole year's credits up front (6,000 on Creator, 30,000 on Pro, 180,000 on Studio) and reset them at renewal, per VEED's FAQ heading How do AI credits work? and Fluxnote's Sep 23, 2026 guide. Prices rendered in Canadian dollars on Oct 9, 2026 and the USD list price was not displayed; VEED's refund terms sit behind a FAQ entry titled What is your refund policy? on the same page.",
  faqs: [
    {
      question: "Is Crayo cheaper than VEED for clipping?",
      answer:
        `It depends on which VEED price you see. Crayo Hobby is ${crayoPlanTerms(hobby)} in US dollars for ${hobby.credits} workflow credits, and one credit is one finished clip. VEED Creator showed CA$29 a month or CA$171 a year on Oct 9, 2026, rendered in Canadian dollars, with 500 credits a month on monthly billing, which is below Crayo's US-dollar sticker on both terms once converted. VEED does not publish how many credits one clip costs, so a per-clip price cannot be computed. If you need the Free plan's 10 minutes a month, VEED is cheaper because Crayo has no free tier.`,
    },
    {
      question: "Which is better for short-form clips from podcasts and streams, Crayo or VEED?",
      answer:
        "For most clippers, Crayo, because clipping is the main product rather than one tool in a suite. AutoClip takes a YouTube or TikTok link or a file up to 3 hours, returns 1 to 20 ranked clips of 15 to 120 seconds, and reframes faces across speaker, streamer, split and full layouts, and every clip opens in the timeline editor on every plan. VEED's AI Clip Generator scores moments 0 to 10 on four factors and feeds a full editor with brand kits and avatars, which suits marketing teams more than solo clippers. No head-to-head test has run yet; one is scheduled on the same podcast file.",
    },
    {
      question: "Does Crayo have a free plan like VEED?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month. VEED (often written VEED.IO) offers a Free plan with 10 minutes of video a month, 720p export, a watermark and 2 minutes of auto-subtitles, per VEED's own article. One VEED reviewer on Sep 28, 2026 wrote that the token allowance was too small to decide, so budget a paid month on either side to judge properly.`,
    },
    {
      question: "Why do VEED's prices look different on every site?",
      answer:
        `Because VEED renders prices with JavaScript and its own pages disagree. VEED's article at ${ARTICLE} lists Lite at $19 a month or $108 a year and Pro at $49 or $288. Fluxnote (https://fluxnote.io/guides/veed-io-pricing-2026, updated Sep 23, 2026) lists Lite at $19 or $12 annual and Pro at $49 or $24 annual with 30,000 AI credits a year forfeited at renewal. Dupple (https://dupple.com/pricing/veed, Sep 24, 2026) lists Lite at $9 and Pro at $24 annual-equivalent. The live page at ${PRICING} on Oct 9, 2026 showed none of these: Creator, Pro and Studio, priced in Canadian dollars from Canada. Check it in your own browser before buying.`,
    },
    {
      question: "How do VEED's AI credits work compared with Crayo's?",
      answer:
        `VEED meters AI features in credits: 500 a month on Creator monthly billing, or 6,000 a year on Creator yearly, 30,000 a year on Pro yearly and 180,000 a year on Studio yearly, per the pricing page on Oct 9, 2026. The yearly figures are granted up front and, per Fluxnote's Sep 23, 2026 guide and VEED's FAQ heading How do AI credits work?, reset at renewal with unused credits forfeited. Crayo meters by finished clip: ${hobby.credits} workflow credits a month on Hobby, reset every month with no rollover, and one-time top-up packs sold separately.`,
    },
    {
      question: "Can I switch from VEED to Crayo, and does Crayo auto-post?",
      answer:
        "Switching is simple and there is nothing to migrate: paste a YouTube or TikTok link or upload the file, pick a layout and intensity, and export at 1080p or open the clips in the timeline editor. Your source files stay yours on both sides. Crayo does not auto-post or schedule; you download the file and post it yourself. VEED does not schedule either, per its tools pages checked Oct 8, 2026, so neither product replaces a scheduler.",
    },
    {
      question: "Does Crayo have avatars, dubbing or voice cloning like VEED?",
      answer:
        "No. Crayo has AI voiceover with hundreds of voices, a voice changer and Enhance Speech, but no avatars, no voice cloning and no dubbing. VEED sells all three: its Pro plan lists translation to 50+ languages and up to 144 hours a year of AI voice as of Oct 9, 2026, and the suite includes avatars and AI video models. If a talking avatar or a dubbed version of each clip is the deciding feature, VEED wins that row outright.",
    },
    {
      question: "Can I get a refund from VEED or from Crayo?",
      answer:
        "Crayo's policy is published and short: all sales are final, no refunds, cancel any time in one click from Settings with the plan active to the end of the cycle. VEED's pricing page has a FAQ entry titled What is your refund policy?, and its text was not captured here, so read it on VEED's FAQ before paying. A Capterra reviewer in May 2026 stated that downloading anything voids VEED's 14-day refund policy, and a Trustpilot reviewer on Oct 5, 2026 wrote that VEED charged for an extra year and refused to refund. Both are reviewer statements, linked in the billing section.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. VEED prices checked Oct 9, 2026 in a browser: Creator CA$29/mo or CA$14/mo billed yearly (CA$171), Pro CA$39/mo or CA$19/mo yearly (CA$229), Studio CA$79/mo or CA$39/mo yearly (CA$465), rendered in Canadian dollars with no USD list price shown.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-capcut", "crayo-vs-descript", "crayo-vs-kapwing"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs veed",
    "veed alternative",
    "veed alternative reddit",
    "veed vs capcut",
    "veed pricing",
    "veed io alternative for shorts",
  ],
};
